import { css } from '@emotion/react';
import { faEye } from '@fortawesome/pro-solid-svg-icons';
import {
  useEffect,
  useMemo, useState,
} from 'react';
import {
  ButtonComponent, ButtonSize, ButtonStyle,
} from '~/_shared/baseComponents/buttons';
import {
  type DropdownOption, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import type { ExportReportTemplateSelectorArea } from '~/exportReport/templateSelector/exportReportTemplateSelector.constants';
import {
  getAgsReportCountryForArea,
  getAreaReportItems, getReportAreaTranslation,
} from '~/exportReport/templateSelector/exportReportTemplateSelector.helpers';
import { ExportReportTemplateSelectorItemDescriptionComponent } from '~/exportReport/templateSelector/exportReportTemplateSelectorItemDescription.component';
import { useExportReport } from '~/exportReport/useExportReport';
import { type ModalProps } from '~/modal/modalType.enum';
import { modalContentStyle } from '~/proximity/edit/proximityEdit.styles';
import { useUserDataActiveAddonsSelector } from '~/store/userData/userData.selectors';

export type ExportReportTemplateSelectorModalProps = ModalProps<{
  reportAreas: ReadonlyArray<ExportReportTemplateSelectorArea>;
}>;

const contentWrapperStyle = css({
  padding: '16px 24px 24px',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 16,
  '@media all and (min-width: 800px)': {
    gridTemplateColumns: 'repeat(3, minmax(200px, 1fr))',
  },
  '@media all and (min-width: 1100px)': {
    gridTemplateColumns: 'repeat(4, minmax(200px, 1fr))',
  },
});

const itemWrapperStyle = (theme: Theme) => css({
  background: theme.backgroundColors.secondary,
  border: `1px solid ${theme.borderColors.primary}`,
  textAlign: 'left',
  padding: 10,
  borderRadius: 4,
  minHeight: 125,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'stretch',
});

const interactiveWrapperStyle = (theme: Theme) => css({
  transition: 'background-color 0.2s',

  '&:hover': {
    background: theme.backgroundColors.secondaryHover,
  },
});

const dropdownWrapperStyle = css({
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  padding: '24px 24px 0',
});

const dropdownStyle = css({
  minWidth: 250,
});

const dropdownTriggerStyle = css({
  height: 32,
});

const sampleButtonWrapperStyle = css({
  marginTop: 'auto',
});

const sampleButtonStyle = css({
  marginTop: 8,
  minWidth: '100%',
});

const sampleButtonIconStyle = css({
  marginRight: 4,
});

export const ExportReportTemplateSelectorModalComponent = ({ onClose, isOpen, reportAreas }: ExportReportTemplateSelectorModalProps) => {
  const [selectedArea, setSelectedArea] = useState<ExportReportTemplateSelectorArea | null>(null);
  const [t] = useTranslation();
  const userActiveAddons = useUserDataActiveAddonsSelector();

  const items = useMemo(() => {
    if (!selectedArea) {
      return [];
    }

    return getAreaReportItems(selectedArea);
  }, [selectedArea]);

  const areaDropdownOptions: ReadonlyArray<DropdownOption<ExportReportTemplateSelectorArea>> = useMemo(() => {
    return reportAreas.map(area => ({
      value: area,
      name: getReportAreaTranslation(area, t),
    }));
  }, [reportAreas, t]);

  const { setReportSettings, setSampleReportSettings } = useExportReport();

  useEffect(() => {
    setSelectedArea(reportAreas[0] ?? null);
  }, [reportAreas]);

  if (!selectedArea) {
    return null;
  }

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      caption={t('Choose Summary report')}
      contentStyle={modalContentStyle}
      maxWidth={976}
    >
      {areaDropdownOptions.length > 1 && (
        <div css={dropdownWrapperStyle}>
          <span>{t('Data Source Country')}</span>

          <RegularDropdownComponent
            css={dropdownStyle}
            triggerStyle={dropdownTriggerStyle}
            value={selectedArea}
            onChange={setSelectedArea}
            options={areaDropdownOptions}
          />
        </div>
      )}

      <ScrollBarComponent type={ScrollbarType.Horizontal}>
        <div css={contentWrapperStyle}>
          {items.map(item => {
            const userHasAddon = item.requiredSubscriptionAddon.some(addon => userActiveAddons.has(addon));

            return userHasAddon ? (
              <button
                key={item.id}
                css={[itemWrapperStyle, interactiveWrapperStyle]}
                onClick={() => setReportSettings(item.template, getAgsReportCountryForArea(selectedArea))}
              >
                <ExportReportTemplateSelectorItemDescriptionComponent item={item} />
              </button>
            ) : (
              <div key={item.id} css={itemWrapperStyle} >
                <ExportReportTemplateSelectorItemDescriptionComponent item={item} />

                <div css={sampleButtonWrapperStyle}>
                  <ButtonComponent
                    onClick={() => setSampleReportSettings(item.template, getAgsReportCountryForArea(selectedArea))}
                    css={sampleButtonStyle}
                    text={t('Preview Sample Report')}
                    prefixIcon={faEye}
                    buttonStyle={ButtonStyle.Quaternary}
                    size={ButtonSize.Small}
                    prefixIconStyle={sampleButtonIconStyle}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </ScrollBarComponent>
    </ModalComponent>
  );
};
